/** Offline visual QA using the production MapView and bundled hex geometry. */
import React from 'react'
import { Stack, useLocalSearchParams } from 'expo-router'
import { Pressable, Text, View } from 'react-native'
import type { GameEvent, MapDef } from '@warcarp/api-types'
import type { HydratedGameState } from '../../../lib/gameState'
import { MapView } from '../../../components/map/MapView'
import { buildActiveGameViewProps } from '../../../preview/buildActiveGameViewProps'
import { game } from '../../../preview/fixtures/cardBonusOnly'
import hex from '../../../assets/maps/the-hex.json'
import ctf from '../../../assets/maps/captureflag.json'
import { PlayerColorProvider } from '../../../lib/playerColor'

const sourceId = 'hex-core'
const targets = ['hex-northeast-1', 'hex-east-1', 'hex-southeast-1', 'hex-southwest-1']
const base = buildActiveGameViewProps(game)
const map = hex as unknown as MapDef
const state: HydratedGameState = {
  ...base.gameState,
  players: ['1', '2', '3', '4'],
  map: {
    ...map,
    viewBox: { x: 382, y: 240, width: 337, height: 320 },
    territories: map.territories.map((t) => ({
      ...t,
      ownerId: t.id === sourceId ? '1' : t.id.includes('east') ? '4' : '',
      armies: t.id === sourceId ? 6 : 3,
    })),
  },
}

export default function TargetGlowPreview() {
  const { attackResult, fortifyResult, map: mapId, legacy, color, glow, neutral, attackerLost, defenderLost, tapSlop } = useLocalSearchParams<{
    attackResult?: string; map?: string; legacy?: string; color?: string; glow?: string
    neutral?: string; attackerLost?: string; defenderLost?: string
    fortifyResult?: string
    tapSlop?: string
  }>()
  const previewMap = React.useMemo(() => tapSlop ? {
    ...state.map,
    viewBox: { x: 0, y: 0, width: 400, height: 400 },
    regions: [],
    territories: [
      { ...state.map.territories[0]!, id: sourceId, shape: 'M100 100H116V116H100Z', position: { x: 108, y: 108 }, badgeAnchor: undefined },
      { ...state.map.territories[0]!, id: 'hex-east-1', shape: 'M140 100H156V116H140Z', position: { x: 148, y: 108 }, badgeAnchor: undefined },
      { ...state.map.territories[0]!, id: 'neighbor', shape: 'M118 100H130V116H118Z', position: { x: 124, y: 108 }, badgeAnchor: undefined },
    ],
  } : mapId === 'captureflag' ? ctf as unknown as MapDef : state.map, [tapSlop, mapId])
  const previewSource = mapId === 'captureflag' ? 't6' : sourceId
  const previewTargets = mapId === 'captureflag'
    ? previewMap.territories.find((t) => t.id === previewSource)!.adjacencies : targets
  const previewState = React.useMemo(() => ({
    ...state,
    map: { ...previewMap, selectionInnerGlow: glow === '1' ? true : legacy ? undefined : previewMap.selectionInnerGlow, territories: previewMap.territories.map((t) => ({
      ...t, ownerId: t.id === previewSource || (fortifyResult && previewTargets.includes(t.id)) ? '1'
        : neutral ? '' : mapId === 'captureflag' ? '4' : t.ownerId,
      armies: t.id === previewSource ? 6 : 3,
    })) },
  }), [mapId, previewMap, previewSource, legacy, glow, neutral, fortifyResult, previewTargets])
  const [selected, setSelected] = React.useState<string | undefined>()
  const [destination, setDestination] = React.useState<string | undefined>()
  const [events, setEvents] = React.useState<GameEvent[]>([])
  const losses = { attackerLost: Number(attackerLost ?? 0), defenderLost: Number(defenderLost ?? 2) }
  return (
    <View style={{ flex: 1 }}>
      <Stack.Screen options={{ title: 'Target inner glow' }} />
      <Text testID="selection-preview-status" style={{ padding: 12 }}>
        {destination ? 'Target selected' : selected ? 'Choose a target' : 'Tap the 6 to show targets'}
      </Text>
      <Pressable accessibilityRole="button" accessibilityLabel="Clear selection"
        onPress={() => { setSelected(undefined); setDestination(undefined) }}>
        <Text style={{ padding: 12 }}>Clear selection</Text>
      </Pressable>
      {fortifyResult && (
        <Pressable accessibilityRole="button" accessibilityLabel="Preview fortify result" onPress={() => {
          if (!selected || !destination) return
          setEvents(prev => [...prev, { kind: 'fortify', player: '1', fromTerritoryId: selected,
            toTerritoryId: destination, armies: 3, ts: Date.now() }])
        }}><Text style={{ padding: 12 }}>Preview fortify result</Text></Pressable>
      )}
      {neutral && (
        <Pressable accessibilityRole="button" accessibilityLabel="Preview attack result" onPress={() => {
          if (!selected || !destination) return
          setEvents(prev => [...prev, {
            kind: 'attack', attacker: '1', defender: '', fromTerritoryId: selected,
            toTerritoryId: destination, roll: { attacker: [6, 5, 4], defender: [2, 1] },
            ...losses, conquered: false, ts: Date.now(),
          }])
        }}><Text style={{ padding: 12 }}>Preview attack result</Text></Pressable>
      )}
      <View style={{ flex: 1 }}>
        <PlayerColorProvider colorBySlot={{ 1: Number(color) || 1 }}>
        <MapView
          state={previewState}
          viewerSlot={1}
          events={events}
          staticFortifyEntry={fortifyResult === 'static' && selected && destination ? {
            fromTerritoryId: selected, toTerritoryId: destination, armies: 3,
            playerColor: '#B91C1C', startedAt: 0,
          } : undefined}
          selectedFillMode="lighten"
          selectedTerritoryId={selected}
          validTargetIds={tapSlop ? [previewSource, ...previewTargets, 'neighbor'] : selected ? previewTargets : [previewSource]}
          secondaryTerritoryId={destination}
          previewTargetIds={selected && !destination ? previewTargets : []}
          staticAttackBadge={attackResult && selected && destination ? {
            attackerTerritoryId: selected,
            defenderTerritoryId: destination,
            ...losses,
            attackerColor: '#b91c1c',
            defenderColor: neutral ? '#888888' : '#166534',
            conquered: false,
            startedAt: 0,
          } : undefined}
          onTerritoryPress={(id) => {
            if (id === previewSource) { setSelected(id); setDestination(undefined) }
            else if (selected && previewTargets.includes(id)) setDestination(id)
            else { setSelected(undefined); setDestination(undefined) }
          }}
        />
        </PlayerColorProvider>
      </View>
    </View>
  )
}
